export const formatDateOnly = (dateInput) => {
  if (!dateInput) return '';
  try {
    const date = new Date(dateInput);
    if (Number.isNaN(date.getTime())) return String(dateInput);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return String(dateInput);
  }
};

export const normalizeToYYYYMMDD = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return formatDateOnly(dateStr);
  const match = String(dateStr).match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (match) {
    const [, day, month, year] = match;
    return `${year}-${month}-${day}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(dateStr))) return String(dateStr);
  return formatDateOnly(dateStr);
};

export const getMapboxToken = () => {
  const day = new Date().getDate();
  const t1 = process.env.REACT_APP_MAPBOX_TOKEN || '';
  const t2 = process.env.REACT_APP_MAPBOX_TOKEN_2 || t1;
  const t3 = process.env.REACT_APP_MAPBOX_TOKEN_3 || t1;
  const t4 = process.env.REACT_APP_MAPBOX_TOKEN_4 || t1;
  const t5 = process.env.REACT_APP_MAPBOX_TOKEN_5 || t1;
  if (day <= 6) return t1;
  if (day <= 12) return t2;
  if (day <= 18) return t3;
  if (day <= 24) return t4;
  return t5;
};

const directionsCache = new Map();

export const getBatchDirections = async (seq, accessToken, profile = 'driving') => {
  if (!accessToken) return seq;

  const cleanSeq = (seq || []).filter((c) =>
    Array.isArray(c)
    && c.length === 2
    && c[0] !== null
    && c[1] !== null
    && !Number.isNaN(c[0])
    && !Number.isNaN(c[1])
  );

  if (cleanSeq.length <= 1) return cleanSeq;

  const dedupedSeq = [];
  for (let i = 0; i < cleanSeq.length; i += 1) {
    if (i === 0) {
      dedupedSeq.push(cleanSeq[i]);
    } else {
      const prev = cleanSeq[i - 1];
      const curr = cleanSeq[i];
      if (Math.abs(prev[0] - curr[0]) > 1e-7 || Math.abs(prev[1] - curr[1]) > 1e-7) {
        dedupedSeq.push(curr);
      }
    }
  }

  if (dedupedSeq.length <= 1) return dedupedSeq;

  const cacheKey = `${profile}|${dedupedSeq.map((c) => `${c[0].toFixed(6)},${c[1].toFixed(6)}`).join(';')}`;
  if (directionsCache.has(cacheKey)) return directionsCache.get(cacheKey);

  const maxChunkSize = 25;
  const promises = [];
  const chunks = [];

  for (let i = 0; i < dedupedSeq.length - 1; i += (maxChunkSize - 1)) {
    const chunk = dedupedSeq.slice(i, i + maxChunkSize);
    if (chunk.length <= 1) break;
    chunks.push(chunk);

    const coordsStr = chunk.map((c) => `${c[0]},${c[1]}`).join(';');
    const url = `https://api.mapbox.com/directions/v5/mapbox/${profile}/${coordsStr}?alternatives=false&geometries=geojson&overview=full&access_token=${accessToken}`;

    promises.push(
      fetch(url)
        .then((r) => {
          if (!r.ok) throw new Error(`Mapbox API error: ${r.statusText}`);
          return r.json();
        })
        .then((j) => j?.routes?.[0]?.geometry?.coordinates || null)
        .catch((e) => {
          console.error('Mapbox Directions chunk failed:', e.message);
          return null;
        })
    );
  }

  const results = await Promise.all(promises);
  const allLegCoords = [];
  for (let i = 0; i < chunks.length; i += 1) {
    const chunk = chunks[i];
    const routeCoords = results[i];
    if (routeCoords && routeCoords.length > 0) {
      if (allLegCoords.length > 0) allLegCoords.push(...routeCoords.slice(1));
      else allLegCoords.push(...routeCoords);
    } else if (allLegCoords.length > 0) {
      allLegCoords.push(...chunk.slice(1));
    } else {
      allLegCoords.push(...chunk);
    }
  }

  directionsCache.set(cacheKey, allLegCoords);
  return allLegCoords;
};

export const filterEnabledSlots = (slots = []) =>
  slots.filter((slot) => slot.enabled !== false && slot.isEnabled !== false);

export const sortSlotsByTime = (slots = []) =>
  [...slots].sort((a, b) => {
    const timeA = a.startTime || '99:99';
    const timeB = b.startTime || '99:99';
    return timeA.localeCompare(timeB);
  });

export const fetchAddressFromPincode = async (pincode) => {
  const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
  const data = await response.json();
  const result = data?.[0];
  if (!result || result.Status !== 'Success' || !result.PostOffice?.length) {
    return null;
  }
  return result.PostOffice.map((po) => ({
    state: po.State || '',
    district: po.District || '',
    country: po.Country || 'India',
    city: po.Name || po.Block || po.Division || '',
  }));
};

export const getWebDetails = () => {
  const userAgent = navigator.userAgent;
  let browserName = 'Unknown';
  let browserVersion = 'Unknown';

  if (userAgent.indexOf('Firefox') > -1) {
    browserName = 'Mozilla Firefox';
    const match = userAgent.match(/Firefox\/(\d+\.\d+)/);
    if (match) browserVersion = match[1];
  } else if (userAgent.indexOf('SamsungBrowser') > -1) {
    browserName = 'Samsung Internet';
    const match = userAgent.match(/SamsungBrowser\/(\d+\.\d+)/);
    if (match) browserVersion = match[1];
  } else if (userAgent.indexOf('Opera') > -1 || userAgent.indexOf('OPR') > -1) {
    browserName = 'Opera';
    const match = userAgent.match(/(?:Opera|OPR)\/(\d+\.\d+)/);
    if (match) browserVersion = match[1];
  } else if (userAgent.indexOf('Trident') > -1) {
    browserName = 'Microsoft Internet Explorer';
    const match = userAgent.match(/rv:(\d+\.\d+)/);
    if (match) browserVersion = match[1];
  } else if (userAgent.indexOf('Edge') > -1 || userAgent.indexOf('Edg') > -1) {
    browserName = 'Microsoft Edge';
    const match = userAgent.match(/(?:Edge|Edg)\/(\d+\.\d+)/);
    if (match) browserVersion = match[1];
  } else if (userAgent.indexOf('Chrome') > -1) {
    browserName = 'Google Chrome';
    const match = userAgent.match(/Chrome\/(\d+\.\d+\.\d+)/);
    if (match) browserVersion = `${match[1]}.`;
  } else if (userAgent.indexOf('Safari') > -1) {
    browserName = 'Apple Safari';
    const match = userAgent.match(/Version\/(\d+\.\d+)/);
    if (match) browserVersion = match[1];
  }

  return {
    webVersion: browserVersion,
    webBrowser: browserName,
    webInfo: userAgent,
  };
};
